<template>
  <div class="max-w-2xl">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-blue">What School District do I live in?</h1>
    <div space-y-10 mt-4>
      <LocationProblem v-if="locationError" :error="locationError" subject="your school district" />
      <LocationLookup v-model="selectedLocation" />
      <div v-if="!locationError && (loading || !data.schoolDistrict)" :class="{ invisible: !loading }" space-y-6>
        <div space-y-3>
          <p h-5 w-30 bg-stone-100 rounded animate-pulse></p>
          <p h-9 w-70 bg-stone-100 rounded animate-pulse></p>
        </div>
        <SchoolDistrictDetailsPlaceholder />
      </div>
      <div space-y-10 v-if="data.schoolDistrict">
        <div v-for="district in data.schoolDistrict" :key="district.id">
          <div space-y-6>
            <div>
              <p font-semibold text-stone-500 text-lg>{{ district.lowGrade }} to {{ district.highGrade }}</p>
              <h2 font-bold text-3xl sm:text-4xl>{{ district.name }}</h2>
            </div>
            <SchoolDistrictDetails :district="district" :loading="loading" />
          </div>
        </div>
      </div>
      <div v-else-if="loading">
        <!-- TODO: add loading placeholder -->
      </div>
      <div space-y-4>
        <Ad ad-slot="1948746426" />
        <h2 text-2xl font-semibold text-stone-500>School District Boundary Map</h2>
        <LazyMapSchoolDistrict
          :location="coordinates"
          :schoolDistrict="data.schoolDistrict"
          @markerMove="setMapLocation"
        />
        <Ad ad-slot="3658187561" />
        <h2 text-2xl font-semibold text-stone-500>Your Location</h2>
        <div grid grid-flow-dense sm:grid-cols-2 gap-2>
          <div v-for="item in SCHOOL_DISTRICT_FIELDS" contents>
            <DataItem
              v-if="!['nativeLands', 'countySubdivision.fullName'].includes(item.field)"
              :loading="loading"
              :label="item.label"
              :value="getDataValue(item.field)"
              valueClass="text-xl sm:text-2xl"
              col-span-2
              sm:col-span-1
              :class="item.class"
            />
            <CountySubdivisionDataItem
              v-else-if="item.field == 'countySubdivision.fullName'"
              :loading="loading"
              :data="data.countySubdivision"
            />
          </div>
        </div>
      </div>
      <div text-stone-5 prose v-if="isLocationSupported">
        <p v-if="!loading && address?.error && !isBot()">An error occurred. Please try again later.</p>
        <p v-else-if="!loading && !data.schoolDistrict && !isBot()">
          We could not determine the School District for your location.
        </p>
        <p>
          By default, the site will gather the gps coordinates directly from your device and then uses that information
          to lookup information about where you are.
        </p>
      </div>
    </div>
    <WikipediaInfo py-10 v-if="placeName" :slug="placeName" />
    <div class="prose">
      <ContentRenderer v-if="page" :value="page" />
    </div>
  </div>
</template>
<script setup>
const { selectedLocation, coordinates, placeName, locationError, setMapLocation, onCoordinatesChange, geolocation } =
  useSelectedLocation();
const { isLocationSupported, startWatching, stopWatching, loading: locationLoading } = geolocation;

const page = await useContent("/school-district");

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `${placeName.value} - ${page.value.title}`;
    }
    return `${page.value.title} - ${page.value.subtitle}`;
  },
});

const { reverseGeocode } = useMapkit();
const address = ref();
const countySubdivision = ref();
const schoolDistrict = ref();

const loading = ref(true);
const watchMode = ref(false);

watch(selectedLocation, () => {
  if (selectedLocation.value == null) {
    return;
  }
  loading.value = true;
  address.value = null;
  countySubdivision.value = null;
  schoolDistrict.value = null;
});

function getDataValue(field) {
  const path = field.split(".");
  let value = data.value;
  for (const key of path) {
    value = value?.[key];
  }
  return value;
}

const data = computed(() => {
  return {
    ...address.value,
    countySubdivision: countySubdivision.value?.data,
    schoolDistrict: schoolDistrict.value?.data,
  };
});

watchEffect(() => {
  watchMode.value ? startWatching() : stopWatching();
});
// there is nothing to load until a location is chosen
watch(locationError, (error) => {
  if (error) {
    loading.value = false;
  }
});

onCoordinatesChange(async () => {
  try {
    // rewritten within Promise.all():
    const [addressResponse, countySubdivisionResponse, schoolDistrictResponse] = await Promise.all([
      reverseGeocode(coordinates.value),
      useFetchCountySubdivision(coordinates.value),
      useFetchSchoolDistrict(coordinates.value),
    ]);
    schoolDistrict.value = await schoolDistrictResponse;
    loading.value = false;
    address.value = addressResponse;
    countySubdivision.value = countySubdivisionResponse;
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
});

</script>
